import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCrmContactsApi,
  fetchCrmMetricsApi,
  fetchCrmDealsApi,
  createCrmContactManualApi,
  createCrmDealApi,
  updateCrmDealStageApi,
  CrmContact,
  CrmMetrics,
  CrmDeal,
} from '../../lib/api';
import {
  Users,
  Search,
  Filter,
  Plus,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Tag,
  CheckCircle2,
  Sparkles,
  Layers,
  ShieldCheck,
  X,
  Award,
  DollarSign,
  TrendingUp,
  ArrowRight,
  ChevronRight,
  Calendar,
  UserCheck,
  RefreshCw,
} from 'lucide-react';

interface AdminCrmModuleProps {
  adminKey: string;
}

const PIPELINE_STAGES: Array<{
  id: string;
  name: string;
  badge: string;
  accent: string;
  border: string;
  dot: string;
}> = [
  {
    id: 'discovery',
    name: '1. Discovery & Lead',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    accent: 'bg-blue-500',
    border: 'border-blue-100',
    dot: 'bg-blue-400',
  },
  {
    id: 'stall_selected',
    name: '2. Stall Selected',
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    accent: 'bg-purple-500',
    border: 'border-purple-100',
    dot: 'bg-purple-400',
  },
  {
    id: 'proposal',
    name: '3. Proposal & Proforma',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    accent: 'bg-amber-500',
    border: 'border-amber-100',
    dot: 'bg-amber-400',
  },
  {
    id: 'negotiation',
    name: '4. Contract Review',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    accent: 'bg-emerald-500',
    border: 'border-emerald-100',
    dot: 'bg-emerald-400',
  },
  {
    id: 'closed_won',
    name: '5. Confirmed & Paid',
    badge: 'bg-emerald-900 text-emerald-100 border-emerald-800',
    accent: 'bg-emerald-600',
    border: 'border-emerald-200',
    dot: 'bg-emerald-300',
  },
];

export function AdminCrmModule({ adminKey }: AdminCrmModuleProps) {
  const queryClient = useQueryClient();

  // Sub-tabs: 'rolodex' | 'pipeline'
  const [activeSubTab, setActiveSubTab] = useState<'rolodex' | 'pipeline'>('rolodex');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<CrmContact | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isNewDealModalOpen, setIsNewDealModalOpen] = useState(false);

  // Form state for adding contact
  const [newContact, setNewContact] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    jobTitle: '',
    role: 'attendee' as 'attendee' | 'exhibitor' | 'speaker' | 'sponsor',
    tier: 'trade_visitor',
    tags: '',
  });

  // Form state for new deal
  const [newDeal, setNewDeal] = useState({
    title: '',
    amountInr: 500000,
    stage: 'discovery',
    probability: 50,
    stallId: '',
    assignedTo: 'Event Sales Team',
    closeDate: '2026-11-15',
  });

  // Query contacts
  const {
    data: contactsData,
    isLoading: isContactsLoading,
    refetch: refetchContacts,
  } = useQuery({
    queryKey: ['admin-crm-contacts', adminKey, roleFilter, searchQuery],
    queryFn: () => fetchCrmContactsApi(adminKey, roleFilter, searchQuery),
    enabled: !!adminKey,
    refetchInterval: 12000,
  });

  // Query metrics
  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['admin-crm-metrics', adminKey],
    queryFn: () => fetchCrmMetricsApi(adminKey),
    enabled: !!adminKey,
    refetchInterval: 15000,
  });

  // Query pipeline deals
  const {
    data: dealsData,
    isLoading: isDealsLoading,
    refetch: refetchDeals,
  } = useQuery({
    queryKey: ['admin-crm-deals', adminKey],
    queryFn: () => fetchCrmDealsApi(adminKey),
    enabled: !!adminKey,
    refetchInterval: 10000,
  });

  // Add contact mutation
  const addContactMutation = useMutation({
    mutationFn: (payload: any) => createCrmContactManualApi(adminKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-crm-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-crm-metrics'] });
      setIsAddModalOpen(false);
      setNewContact({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        jobTitle: '',
        role: 'attendee',
        tier: 'trade_visitor',
        tags: '',
      });
    },
  });

  // Create deal mutation
  const createDealMutation = useMutation({
    mutationFn: (payload: any) => createCrmDealApi(adminKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-crm-deals'] });
      setIsNewDealModalOpen(false);
      setNewDeal({
        title: '',
        amountInr: 500000,
        stage: 'discovery',
        probability: 50,
        stallId: '',
        assignedTo: 'Event Sales Team',
        closeDate: '2026-11-15',
      });
    },
  });

  // Update deal stage mutation
  const updateStageMutation = useMutation({
    mutationFn: ({ dealId, stage }: { dealId: string; stage: string }) =>
      updateCrmDealStageApi(adminKey, dealId, stage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-crm-deals'] });
    },
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tagArray = newContact.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    addContactMutation.mutate({
      fullName: newContact.fullName,
      email: newContact.email,
      phone: newContact.phone || undefined,
      companyName: newContact.companyName || undefined,
      jobTitle: newContact.jobTitle || undefined,
      role: newContact.role,
      tier: newContact.tier || undefined,
      tags: tagArray,
    });
  };

  const handleDealSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createDealMutation.mutate({
      title: newDeal.title,
      amountInr: Number(newDeal.amountInr),
      stage: newDeal.stage,
      probability: Number(newDeal.probability),
      stallId: newDeal.stallId || undefined,
      closeDate: newDeal.closeDate || undefined,
      assignedTo: newDeal.assignedTo || undefined,
    });
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'exhibitor':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'speaker':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'sponsor':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'attendee':
      default:
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };

  const contacts = contactsData?.contacts || [];
  const metrics = metricsData?.metrics;
  const deals = dealsData?.deals || [];

  // Calculate pipeline totals
  const totalPipelineInr = deals.reduce((sum, d) => sum + Number(d.amountInr || 0), 0);
  const wonPipelineInr = deals
    .filter((d) => d.stage === 'closed_won')
    .reduce((sum, d) => sum + Number(d.amountInr || 0), 0);

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-emerald-100/40 via-transparent to-transparent pointer-events-none rounded-full blur-3xl -mr-20 -mt-20" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-emerald-50 text-[#0A3D27] text-xs font-bold border border-emerald-200/80 flex items-center gap-1.5 shadow-2xs">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                <span>Unified B2B CRM Engine</span>
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                <span>Supabase PostgreSQL</span>
              </span>
            </div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
              CRM & Commercial Deal Pipeline
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl">
              Centralized B2B rolodex, exhibitor lead tracking, and commercial booth sponsorship pipeline for IIAB 2027.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {activeSubTab === 'rolodex' ? (
              <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-2xl text-xs font-bold flex items-center gap-2 transition-all shadow-xs cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Add Contact</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsNewDealModalOpen(true)}
                className="px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-2xl text-xs font-bold flex items-center gap-2 transition-all shadow-xs cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>New Deal</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                refetchContacts();
                refetchMetrics();
                refetchDeals();
              }}
              title="Refresh CRM Data"
              className="p-2.5 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-2xl transition-colors cursor-pointer"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* View Switcher Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-100 pt-6 mt-2">
          <button
            type="button"
            onClick={() => setActiveSubTab('rolodex')}
            className={`flex items-center gap-2 pb-3 px-2 text-xs font-bold transition-colors cursor-pointer relative ${
              activeSubTab === 'rolodex'
                ? 'text-[#0A3D27]'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>B2B Rolodex (Contacts)</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-mono">
              {metrics?.totalContacts ?? contacts.length}
            </span>
            {activeSubTab === 'rolodex' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0A3D27] rounded-full" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('pipeline')}
            className={`flex items-center gap-2 pb-3 px-2 text-xs font-bold transition-colors cursor-pointer relative ${
              activeSubTab === 'pipeline'
                ? 'text-[#0A3D27]'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Layers className="w-4 h-4" />
            <span>Deal Pipeline (Kanban)</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-mono font-bold">
              {deals.length} Deals
            </span>
            {activeSubTab === 'pipeline' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0A3D27] rounded-full" />
            )}
          </button>
        </div>
      </div>

      {/* SUB-TAB 1: B2B ROLODEX (TABLE VIEW) */}
      {activeSubTab === 'rolodex' && (
        <div className="space-y-6">
          {/* Key Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Total Contacts
              </span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-black text-slate-900">
                  {metrics?.totalContacts ?? contacts.length}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-purple-600 uppercase tracking-wider block">
                Exhibitors
              </span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-black text-slate-900">
                  {metrics?.exhibitorsCount ?? contacts.filter((c) => c.role === 'exhibitor').length}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider block">
                Delegates
              </span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-black text-slate-900">
                  {metrics?.delegatesCount ?? contacts.filter((c) => c.role === 'attendee').length}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider block">
                Speakers
              </span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-black text-slate-900">
                  {metrics?.speakersCount ?? contacts.filter((c) => c.role === 'speaker').length}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider block">
                Trade Visitors
              </span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-black text-slate-900">
                  {metrics?.tradeVisitorsCount ?? 0}
                </span>
              </div>
            </div>
          </div>

          {/* Search & Filter Toolbar */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, company, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" />
                Role:
              </span>
              {['all', 'exhibitor', 'attendee', 'speaker', 'sponsor'].map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setRoleFilter(role)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize transition-all cursor-pointer ${
                    roleFilter === role
                      ? 'bg-[#0A3D27] text-white shadow-2xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          {/* Contacts Table */}
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                    <th className="py-3 px-4">Contact</th>
                    <th className="py-3 px-4">Company & Designation</th>
                    <th className="py-3 px-4">Role & Tier</th>
                    <th className="py-3 px-4">Tags</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {isContactsLoading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-400">
                        Loading unified CRM contacts...
                      </td>
                    </tr>
                  ) : contacts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-400">
                        No contacts found matching your query.
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => (
                      <tr
                        key={contact.id}
                        onClick={() => setSelectedContact(contact)}
                        className="hover:bg-slate-50/70 transition-colors cursor-pointer"
                      >
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-900">{contact.name}</div>
                          <div className="text-[11px] text-slate-500">{contact.email}</div>
                          {contact.phone && (
                            <div className="text-[10px] text-slate-400 font-mono">{contact.phone}</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-slate-800">
                            {contact.companyName || '—'}
                          </div>
                          <div className="text-[11px] text-slate-500">{contact.jobTitle || '—'}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1 items-start">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border capitalize ${getRoleBadgeStyle(
                                contact.role
                              )}`}
                            >
                              {contact.role}
                            </span>
                            {contact.tier && (
                              <span className="text-[10px] text-slate-500 capitalize">
                                {contact.tier.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {contact.tags.slice(0, 3).map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-mono"
                              >
                                {tag}
                              </span>
                            ))}
                            {contact.tags.length > 3 && (
                              <span className="text-[10px] text-slate-400">
                                +{contact.tags.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 capitalize">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            {contact.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContact(contact);
                            }}
                            className="px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: DEAL PIPELINE (KANBAN BOARD) */}
      {activeSubTab === 'pipeline' && (
        <div className="space-y-6">
          {/* Pipeline Financial Summary Bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Total Pipeline Value
              </span>
              <div className="text-2xl font-black text-slate-900 mt-1">
                ₹{totalPipelineInr.toLocaleString('en-IN')}
              </div>
              <span className="text-[11px] text-slate-500 font-medium">
                Across {deals.length} active sponsorships & stands
              </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider block">
                Confirmed (Closed Won)
              </span>
              <div className="text-2xl font-black text-emerald-950 mt-1">
                ₹{wonPipelineInr.toLocaleString('en-IN')}
              </div>
              <span className="text-[11px] text-emerald-700 font-medium">
                100% contracted & invoicing triggered
              </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider block">
                In Negotiation
              </span>
              <div className="text-2xl font-black text-slate-900 mt-1">
                ₹
                {deals
                  .filter((d) => d.stage === 'negotiation' || d.stage === 'proposal')
                  .reduce((sum, d) => sum + Number(d.amountInr || 0), 0)
                  .toLocaleString('en-IN')}
              </div>
              <span className="text-[11px] text-slate-500 font-medium">
                High conversion probability (60–85%)
              </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-purple-600 uppercase tracking-wider block">
                Pipeline Health
              </span>
              <div className="text-2xl font-black text-purple-950 mt-1">
                {Math.round((wonPipelineInr / (totalPipelineInr || 1)) * 100)}%
              </div>
              <span className="text-[11px] text-purple-700 font-medium">
                Target achievement rate
              </span>
            </div>
          </div>

          {/* Kanban Columns Grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-x-auto pb-4">
            {PIPELINE_STAGES.map((col) => {
              const colDeals = deals.filter((d) => d.stage === col.id);
              const colTotal = colDeals.reduce((sum, d) => sum + Number(d.amountInr || 0), 0);

              return (
                <div
                  key={col.id}
                  className={`bg-slate-50/70 border ${col.border} rounded-3xl p-4 flex flex-col min-w-[260px]`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200/80">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                      <h4 className="text-xs font-black text-slate-900 tracking-tight">
                        {col.name}
                      </h4>
                    </div>
                    <span className="px-2 py-0.5 rounded-full bg-white text-slate-700 text-[10px] font-bold border border-slate-200 shadow-2xs">
                      {colDeals.length}
                    </span>
                  </div>

                  <div className="text-[11px] text-slate-500 font-mono font-bold mb-3">
                    ₹{colTotal.toLocaleString('en-IN')}
                  </div>

                  {/* Deals Cards in Column */}
                  <div className="space-y-3 flex-1">
                    {colDeals.length === 0 ? (
                      <div className="py-8 text-center text-[11px] text-slate-400 font-medium border-2 border-dashed border-slate-200/80 rounded-2xl">
                        No deals in this stage
                      </div>
                    ) : (
                      colDeals.map((deal) => (
                        <div
                          key={deal.id}
                          className="bg-white border border-slate-200 rounded-2xl p-3.5 shadow-2xs hover:shadow-xs transition-shadow space-y-2.5"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h5 className="font-bold text-xs text-slate-900 leading-snug">
                              {deal.title}
                            </h5>
                          </div>

                          <div className="flex items-center justify-between text-xs pt-1">
                            <span className="font-mono font-black text-[#0A3D27]">
                              ₹{Number(deal.amountInr).toLocaleString('en-IN')}
                            </span>
                            {deal.stallId && (
                              <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 font-mono text-[10px] font-bold border border-purple-200">
                                {deal.stallId}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-100">
                            <span>{deal.assignedTo || 'Sales Team'}</span>
                            <span className="font-mono font-semibold">{deal.probability}% Prob</span>
                          </div>

                          {/* Stage Transition Quick Action */}
                          {col.id !== 'closed_won' && (
                            <button
                              type="button"
                              onClick={() => {
                                const currentIndex = PIPELINE_STAGES.findIndex((s) => s.id === col.id);
                                if (currentIndex < PIPELINE_STAGES.length - 1) {
                                  const nextStage = PIPELINE_STAGES[currentIndex + 1].id;
                                  updateStageMutation.mutate({ dealId: deal.id, stage: nextStage });
                                }
                              }}
                              className="w-full mt-2 py-1 px-2 rounded-xl bg-slate-50 hover:bg-[#0A3D27] text-slate-600 hover:text-white border border-slate-200 text-[10px] font-bold flex items-center justify-center gap-1 transition-colors cursor-pointer"
                            >
                              <span>Advance Stage</span>
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Slide-over Contact Details Drawer */}
      {selectedContact && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs">
          <div className="w-full max-w-md bg-white h-full shadow-2xl p-6 overflow-y-auto space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="space-y-1">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Contact Dossier
                </span>
                <h3 className="text-xl font-black text-slate-900">{selectedContact.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedContact(null)}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
                <span className="text-[11px] font-bold text-slate-500 uppercase">Role & Priority</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold border capitalize ${getRoleBadgeStyle(
                      selectedContact.role
                    )}`}
                  >
                    {selectedContact.role}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 capitalize">
                    {selectedContact.status}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-xs text-slate-700">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${selectedContact.email}`} className="hover:underline font-medium">
                    {selectedContact.email}
                  </a>
                </div>
                {selectedContact.phone && (
                  <div className="flex items-center gap-3 text-xs text-slate-700">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="font-mono">{selectedContact.phone}</span>
                  </div>
                )}
                {selectedContact.companyName && (
                  <div className="flex items-center gap-3 text-xs text-slate-700">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span>{selectedContact.companyName}</span>
                  </div>
                )}
                {selectedContact.jobTitle && (
                  <div className="flex items-center gap-3 text-xs text-slate-700">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span>{selectedContact.jobTitle}</span>
                  </div>
                )}
              </div>

              <div>
                <span className="text-xs font-bold text-slate-800 block mb-2">Audience Tags:</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedContact.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-mono"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {selectedContact.metadata && Object.keys(selectedContact.metadata).length > 0 && (
                <div>
                  <span className="text-xs font-bold text-slate-800 block mb-2">Metadata Attributes:</span>
                  <pre className="p-3 bg-slate-900 text-emerald-400 rounded-xl text-[11px] font-mono overflow-x-auto">
                    {JSON.stringify(selectedContact.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-black text-slate-900">Add B2B Contact</h3>
                <p className="text-xs text-slate-500">
                  Manually register an exhibitor, delegate, or sponsor contact.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={newContact.fullName}
                  onChange={(e) => setNewContact({ ...newContact, fullName: e.target.value })}
                  placeholder="e.g. Ramesh Kulkarni"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    placeholder="ramesh@agri.com"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    placeholder="+91 98200 12345"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Company Name</label>
                  <input
                    type="text"
                    value={newContact.companyName}
                    onChange={(e) => setNewContact({ ...newContact, companyName: e.target.value })}
                    placeholder="e.g. Mahindra Agri"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Job Title</label>
                  <input
                    type="text"
                    value={newContact.jobTitle}
                    onChange={(e) => setNewContact({ ...newContact, jobTitle: e.target.value })}
                    placeholder="e.g. Managing Director"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Role</label>
                  <select
                    value={newContact.role}
                    onChange={(e) => setNewContact({ ...newContact, role: e.target.value as any })}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  >
                    <option value="attendee">Attendee</option>
                    <option value="exhibitor">Exhibitor</option>
                    <option value="speaker">Speaker</option>
                    <option value="sponsor">Sponsor</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Tier / Priority</label>
                  <input
                    type="text"
                    value={newContact.tier}
                    onChange={(e) => setNewContact({ ...newContact, tier: e.target.value })}
                    placeholder="trade_visitor, vip..."
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">Tags (Comma-separated)</label>
                <input
                  type="text"
                  value={newContact.tags}
                  onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })}
                  placeholder="commercial_stall, keynote, priority"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addContactMutation.isPending}
                  className="px-5 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                >
                  {addContactMutation.isPending ? 'Saving...' : 'Save Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Deal Modal */}
      {isNewDealModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-black text-slate-900">Add Deal to Pipeline</h3>
                <p className="text-xs text-slate-500">
                  Track a commercial stall booking or title sponsorship inquiry.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsNewDealModalOpen(false)}
                className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleDealSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">Deal Title *</label>
                <input
                  type="text"
                  required
                  value={newDeal.title}
                  onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                  placeholder="e.g. Mahindra Pavilion 3 Mega Stand"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Amount (INR) *</label>
                  <input
                    type="number"
                    required
                    min={1000}
                    value={newDeal.amountInr}
                    onChange={(e) => setNewDeal({ ...newDeal, amountInr: Number(e.target.value) })}
                    placeholder="850000"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Stall ID (Optional)</label>
                  <input
                    type="text"
                    value={newDeal.stallId}
                    onChange={(e) => setNewDeal({ ...newDeal, stallId: e.target.value })}
                    placeholder="e.g. SB22 or PB10"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Pipeline Stage</label>
                  <select
                    value={newDeal.stage}
                    onChange={(e) => setNewDeal({ ...newDeal, stage: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  >
                    {PIPELINE_STAGES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Assigned Sales Rep</label>
                  <input
                    type="text"
                    value={newDeal.assignedTo}
                    onChange={(e) => setNewDeal({ ...newDeal, assignedTo: e.target.value })}
                    placeholder="Aditi Rao, Rohan Gupta..."
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsNewDealModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createDealMutation.isPending}
                  className="px-5 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                >
                  {createDealMutation.isPending ? 'Creating Deal...' : 'Create Deal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
